import { useState } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { useRef } from 'react';
import { PADDLES, TEST_SITES, DIMENSIONS, MASS_BREAKDOWN, MARKET_BANDS, TECH_IMG } from '../../data/technicals';

const RED = PADDLES.hexcore.accent;
const BLUE = PADDLES.profoam.accent;
const mono = { fontFamily: 'ui-monospace, monospace' };

// Shared planform outline, viewBox 0 0 200 430 — 1 unit ≈ 1 mm, butt at y=417
const PADDLE_PATH =
  'M 100 2 C 150 2, 194 10, 194 60 L 194 215 C 194 250, 175 275, 150 285 L 118 298 L 118 408 Q 118 417 108 417 L 92 417 Q 82 417 82 408 L 82 298 L 50 285 C 25 275, 6 250, 6 215 L 6 60 C 6 10, 50 2, 100 2 Z';

const Outline = ({ children, className = '' }) => (
  <svg viewBox="0 0 200 430" className={`w-full h-auto ${className}`}>
    <defs>
      <pattern id="pf-carbon" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
        <rect width="6" height="6" fill="#141417" /><rect width="3" height="6" fill="#1c1c20" />
      </pattern>
    </defs>
    <path d={PADDLE_PATH} fill="url(#pf-carbon)" stroke="rgba(255,255,255,0.35)" strokeWidth="1.5" />
    <path d="M 100 2 C 150 2, 194 10, 194 60 L 194 215 C 194 250, 175 275, 150 285 L 118 298 L 82 298 L 50 285 C 25 275, 6 250, 6 215 L 6 60 C 6 10, 50 2, 100 2 Z" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="6" />
    {children}
  </svg>
);

// ---------- Interactive "Where we measured" map (Figure 4) ----------
export const TestMap = () => {
  const [active, setActive] = useState(1);
  const site = TEST_SITES.find((s) => s.id === active);
  return (
    <div className="grid md:grid-cols-12 gap-6 items-center" data-testid="test-map">
      <div className="md:col-span-5 max-w-[250px] mx-auto w-full">
        <Outline>
          {/* sweet spot start */}
          <line x1="12" x2="188" y1={417 - DIMENSIONS.sweetSpotStart} y2={417 - DIMENSIONS.sweetSpotStart} stroke="rgba(255,255,255,0.25)" strokeDasharray="2 3" />
          {TEST_SITES.map((s) => {
            const on = s.id === active;
            return (
              <g key={s.id} className="cursor-pointer" onClick={() => setActive(s.id)} data-testid={`test-site-${s.id}`}>
                {on && <circle cx={s.x} cy={s.y} r="16" fill={RED} opacity="0.18"><animate attributeName="r" values="12;20;12" dur="2s" repeatCount="indefinite" /></circle>}
                <circle cx={s.x} cy={s.y} r="9" fill={on ? RED : '#0B0B0C'} stroke={on ? '#fff' : 'rgba(255,255,255,0.6)'} strokeWidth="1.5" />
                <text x={s.x} y={s.y + 3.5} textAnchor="middle" fill="#fff" style={{ fontSize: 9, fontWeight: 700, ...mono }}>{s.id}</text>
              </g>
            );
          })}
        </Outline>
      </div>
      <div className="md:col-span-7">
        <div className="flex flex-wrap gap-2 mb-5">
          {TEST_SITES.map((s) => (
            <button
              key={s.id}
              onClick={() => setActive(s.id)}
              className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${s.id === active ? 'border-white bg-white text-black' : 'border-white/15 text-zinc-300 hover:border-white/40'}`}
              data-testid={`test-chip-${s.id}`}
            >
              <span className={`w-4 h-4 rounded-full text-[9px] flex items-center justify-center ${s.id === active ? 'bg-black text-white' : 'bg-white/10'}`} style={mono}>{s.id}</span>
              {s.short}
            </button>
          ))}
        </div>
        <AnimatePresence mode="wait">
          <motion.div key={active} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.22 }} className="rounded-2xl border border-white/10 bg-white/[0.03] p-6" data-testid="test-site-detail">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">Test {site.id} of 6</p>
            <h3 className="mt-2 font-heading font-bold text-xl sm:text-2xl text-white">{site.title}</h3>
            <p className="mt-3 text-sm sm:text-base text-zinc-300 leading-relaxed">{site.body}</p>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

// ---------- Dimensions & balance on the shared planform ----------
export const PlanformDims = () => {
  const [which, setWhich] = useState('hexcore');
  const p = PADDLES[which];
  const d = DIMENSIONS;
  const yBal = 417 - d.balance[which];
  const yBase = 417 - d.balance.hexcore;
  const ySweet = 417 - d.sweetSpotStart;
  return (
    <div className="grid md:grid-cols-12 gap-6 items-center" data-testid="planform-dims">
      <div className="md:col-span-5 max-w-[250px] mx-auto w-full">
        <Outline>
          {/* head width */}
          <line x1="6" x2="194" y1="24" y2="24" stroke="rgba(255,255,255,0.5)" />
          <line x1="6" x2="6" y1="19" y2="29" stroke="rgba(255,255,255,0.5)" /><line x1="194" x2="194" y1="19" y2="29" stroke="rgba(255,255,255,0.5)" />
          <rect x="66" y="16" width="68" height="15" rx="7" fill="#0B0B0C" stroke="rgba(255,255,255,0.2)" />
          <text x="100" y="27" textAnchor="middle" fill="#fff" style={{ fontSize: 8.5, fontWeight: 700, ...mono }}>{d.headWidth[which].toFixed(0)} mm</text>
          {/* sweet spot */}
          <line x1="12" x2="188" y1={ySweet} y2={ySweet} stroke="rgba(255,255,255,0.3)" strokeDasharray="2 3" />
          <text x="188" y={ySweet - 4} textAnchor="end" fill="rgba(255,255,255,0.6)" style={{ fontSize: 7.5, ...mono, letterSpacing: 1 }}>SWEET SPOT</text>
          {/* balance point — drawn at the HexCore position, group slides to the active paddle */}
          <motion.g initial={false} animate={{ y: yBal - yBase }} transition={{ type: 'spring', stiffness: 170, damping: 22 }}>
            <line x1="30" x2="170" y1={yBase} y2={yBase} stroke={p.accent} strokeWidth="1.5" />
            <circle cx="100" cy={yBase} r="5" fill={p.accent} stroke="#fff" strokeWidth="1.5" />
            <text x="100" y={yBase + 16} textAnchor="middle" fill={p.accent} style={{ fontSize: 8.5, fontWeight: 700, ...mono }}>BALANCE {d.balance[which]} MM</text>
            <line x1="196" x2="196" y1={yBase} y2={417 + (yBase - yBal)} stroke={p.accent} strokeWidth="1" strokeDasharray="2 2" />
          </motion.g>
          <line x1="192" x2="200" y1="417" y2="417" stroke={p.accent} />
        </Outline>
      </div>
      <div className="md:col-span-7">
        <div className="inline-flex rounded-full border border-white/15 p-1 mb-5" data-testid="planform-toggle">
          {['hexcore', 'profoam'].map((k) => (
            <button key={k} onClick={() => setWhich(k)} className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-[0.15em] transition-colors ${which === k ? 'text-white' : 'text-zinc-400 hover:text-white'}`} style={which === k ? { backgroundColor: PADDLES[k].accent } : undefined} data-testid={`planform-toggle-${k}`}>
              {PADDLES[k].name}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3">
          {[d.length, d.headWidth, d.thickness, d.balance].map((m) => (
            <div key={m.label} className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
              <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500">{m.label}</p>
              <p className="mt-1.5 font-mono text-xl sm:text-2xl text-white">{m[which]} <span className="text-xs text-zinc-500">{m.unit}</span></p>
            </div>
          ))}
        </div>
        <p className="mt-4 text-sm text-zinc-400">Both balance a little below the sweet spot — a light, easy feel in the hand. {d.consistency}</p>
      </div>
    </div>
  );
};

// ---------- Where the grams live ----------
export const MassBreakdown = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '0px 0px -5% 0px' });
  const colors = { body: null, guard: 'rgba(255,255,255,0.35)', handle: 'rgba(255,255,255,0.16)' };
  return (
    <div ref={ref} className="grid lg:grid-cols-12 gap-6 items-center" data-testid="mass-breakdown">
      <div className="lg:col-span-7 space-y-7">
        {['hexcore', 'profoam'].map((k) => {
          const p = PADDLES[k]; const mb = MASS_BREAKDOWN[k];
          const total = mb.parts.reduce((a, b) => a + b.g, 0);
          return (
            <div key={k} data-testid={`mass-row-${k}`}>
              <div className="flex items-baseline justify-between mb-2">
                <span className="font-display text-xl uppercase tracking-tight" style={{ color: p.accent }}>{p.name}</span>
                <span className="font-mono text-sm text-white">{mb.whole} <span className="text-xs text-zinc-500">g whole</span></span>
              </div>
              <div className="flex h-9 rounded-lg overflow-hidden border border-white/10 bg-white/[0.04]">
                {mb.parts.map((part, i) => (
                  <motion.div
                    key={part.key}
                    className="h-full relative"
                    style={{ background: colors[part.key] || p.accent }}
                    initial={{ width: 0 }}
                    animate={inView ? { width: `${(part.g / total) * 100}%` } : {}}
                    transition={{ duration: 1, delay: 0.1 * i, ease: [0.22, 1, 0.36, 1] }}
                    title={`${part.label}: ${part.g} g`}
                  >
                    <span className="absolute inset-0 flex items-center justify-center font-mono text-[11px] text-white/90">{part.g} g</span>
                  </motion.div>
                ))}
              </div>
            </div>
          );
        })}
        <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs text-zinc-400">
          <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm bg-gradient-to-r from-[#E02128] to-[#1E6FD9]" /> Body — faces + core</span>
          <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm" style={{ background: colors.guard }} /> Edge guard</span>
          <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm" style={{ background: colors.handle }} /> Handle & grip</span>
        </div>
        <p className="text-sm sm:text-base text-zinc-200 leading-relaxed border-l-2 border-white/20 pl-4" data-testid="mass-insight">{MASS_BREAKDOWN.insight}</p>
      </div>
      <div className="lg:col-span-5 grid grid-cols-2 gap-3">
        {[{ k: 'hexcore', src: TECH_IMG.teardownHex }, { k: 'profoam', src: TECH_IMG.teardownPro }].map(({ k, src }) => (
          <figure key={k} className="relative rounded-xl overflow-hidden border border-white/10">
            <img src={src} alt={`${PADDLES[k].fullName} taken apart: body, edge guard, grip and handle parts laid out for weighing`} className="w-full h-full object-cover aspect-[3/4]" loading="lazy" />
            <figcaption className="absolute bottom-0 inset-x-0 p-2 bg-gradient-to-t from-black/80 to-transparent">
              <span className="font-mono text-[9px] uppercase tracking-[0.2em]" style={{ color: PADDLES[k].accent }}>{PADDLES[k].name} · teardown</span>
            </figcaption>
          </figure>
        ))}
      </div>
    </div>
  );
};

// ---------- Measured against published paddles ----------
export const MarketGauges = () => (
  <div className="space-y-10" data-testid="market-gauges">
    {MARKET_BANDS.map((g) => {
      const pos = (v) => ((v - g.min) / (g.max - g.min)) * 100;
      return (
        <div key={g.key} data-testid={`market-gauge-${g.key}`}>
          <div className="flex items-baseline justify-between mb-3">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-400">{g.label}</p>
            <p className="font-mono text-[10px] text-zinc-500">{g.unit}</p>
          </div>
          <div className="space-y-2">
            {g.bands.map((b) => (
              <div key={b.label} className="grid sm:grid-cols-[200px_1fr] gap-1 sm:gap-4 items-center">
                <span className="font-mono text-[9px] uppercase tracking-wider text-zinc-500 leading-tight">{b.label}<span className="text-zinc-600 normal-case tracking-normal"> · {b.from}–{b.to} {g.unit}</span></span>
                <div className="relative h-3">
                  <div className="absolute inset-x-0 top-1/2 h-px bg-white/10" />
                  <div className="absolute h-3 rounded-sm bg-white/15 border border-white/15" style={{ left: `${pos(b.from)}%`, width: `${pos(b.to) - pos(b.from)}%` }} />
                </div>
              </div>
            ))}
            <div className="grid sm:grid-cols-[200px_1fr] gap-1 sm:gap-4 items-center">
              <span className="font-mono text-[9px] uppercase tracking-wider text-zinc-300 leading-tight">DYSSC · measured</span>
              <div className="relative h-16">
                <div className="absolute inset-x-0 top-1/2 h-px bg-white/25" />
                {['hexcore', 'profoam'].map((k, i) => (
                  <div key={k} className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2" style={{ left: `${pos(g[k])}%` }} data-testid={`market-marker-${g.key}-${k}`}>
                    <div className="w-4 h-4 rounded-full border-2 border-white shadow-[0_0_14px_rgba(0,0,0,0.6)]" style={{ background: PADDLES[k].accent }} />
                    <span className="absolute left-1/2 -translate-x-1/2 font-mono text-[10px] font-bold whitespace-nowrap" style={{ color: PADDLES[k].accent, top: i === 0 ? -22 : 20 }}>{PADDLES[k].name} {g[k]}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <p className="mt-3 text-sm text-zinc-300">{g.note}</p>
        </div>
      );
    })}
  </div>
);
